import Component from 'flarum/common/Component';
import app from 'flarum/common/app';

const DISMISS_KEY = 'alwarithun_pwa_dismissed';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days cooldown

export default class PwaInstallPrompt extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.deferredPrompt = null;
    this.showGuide = false;
    this.isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Check visibility immediately on init
    this.visible = !this.isStandalone() && !this.isDismissed() && this.isMobileDevice();

    this.checkAndInit();

    // Expose debug helpers on window for live testing and inspection
    window.AlwarithunPwa = {
      instance: this,
      show: () => {
        this.visible = true;
        m.redraw();
        console.log('[Alwarithun PWA Prompt] Force shown');
      },
      hide: () => {
        this.visible = false;
        m.redraw();
      },
      reset: () => {
        try {
          localStorage.removeItem(DISMISS_KEY);
        } catch (e) {}
        this.visible = this.isMobileDevice();
        m.redraw();
        console.log('[Alwarithun PWA Prompt] Dismissal reset');
      },
      status: () => ({
        visible: this.visible,
        standalone: this.isStandalone(),
        dismissed: this.isDismissed(),
        mobile: this.isMobileDevice(),
        deferredPrompt: !!this.deferredPrompt,
      }),
    };

    console.log('[Alwarithun PWA Prompt] Initialized:', window.AlwarithunPwa.status());
  }

  isStandalone() {
    try {
      return (
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        window.navigator.standalone === true ||
        (document.referrer && document.referrer.includes('android-app://'))
      );
    } catch (e) {
      return false;
    }
  }

  isMobileDevice() {
    try {
      return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent) ||
        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
        window.innerWidth <= 768
      );
    } catch (e) {
      return false;
    }
  }

  isDismissed() {
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (!dismissedAt) return false;
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      return elapsed < COOLDOWN_MS;
    } catch (e) {
      return false;
    }
  }

  checkAndInit() {
    if (this.isStandalone()) {
      return;
    }

    // Android / Chromium beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      if (!this.isDismissed() && this.isMobileDevice()) {
        this.visible = true;
        m.redraw();
      }
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.visible = false;
      m.redraw();
    });

    // Fallback timer to ensure visibility after dynamic layout adjustments
    setTimeout(() => {
      if (!this.isStandalone() && !this.isDismissed() && this.isMobileDevice() && !this.visible) {
        this.visible = true;
        m.redraw();
      }
    }, 800);
  }

  getAppName() {
    try {
      return (
        (app.forum && app.forum.attribute('askvortsov-pwa.short_name')) ||
        (app.forum && app.forum.attribute('askvortsov-pwa.long_name')) ||
        (app.forum && app.forum.attribute('title')) ||
        'الوارثون'
      );
    } catch (e) {
      return 'الوارثون';
    }
  }

  getAppDescription() {
    try {
      return (
        (app.forum && app.forum.attribute('askvortsov-pwa.description')) ||
        'تطبيق المنتدى لتصفح أسرع وتجربة أفضل على هاتفك'
      );
    } catch (e) {
      return 'تطبيق المنتدى لتصفح أسرع وتجربة أفضل على هاتفك';
    }
  }

  getAppIcon() {
    try {
      return (
        (app.forum && app.forum.attribute('askvortsov-pwa.icon_url')) ||
        (document.querySelector('link[rel="apple-touch-icon"]') && document.querySelector('link[rel="apple-touch-icon"]').getAttribute('href')) ||
        (document.querySelector('link[rel="icon"]') && document.querySelector('link[rel="icon"]').getAttribute('href')) ||
        (app.forum && app.forum.attribute('faviconUrl')) ||
        (app.forum && app.forum.attribute('logoUrl')) ||
        null
      );
    } catch (e) {
      return null;
    }
  }

  install() {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult && choiceResult.outcome === 'accepted') {
          this.visible = false;
        }
        this.deferredPrompt = null;
        m.redraw();
      });
    } else {
      this.showGuide = !this.showGuide;
      m.redraw();
    }
  }

  dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch (e) {}
    this.visible = false;
    this.showGuide = false;
    m.redraw();
  }

  view() {
    if (!this.visible || this.isStandalone()) {
      return null;
    }

    const appName = this.getAppName();
    const appDesc = this.getAppDescription();
    const appIcon = this.getAppIcon();
    const direction = document.documentElement.getAttribute('dir') || 'rtl';

    return (
      <div className="PwaInstallPrompt-wrapper" dir={direction}>
        <div className="PwaInstallPrompt-card">
          <button
            type="button"
            className="PwaInstallPrompt-close"
            onclick={() => this.dismiss()}
            aria-label="إغلاق"
          >
            <i className="fas fa-times" />
          </button>

          <div className="PwaInstallPrompt-header">
            <div className="PwaInstallPrompt-icon-wrapper">
              {appIcon ? (
                <img src={appIcon} alt={appName} className="PwaInstallPrompt-icon" />
              ) : (
                <div className="PwaInstallPrompt-icon-placeholder">
                  <i className="fas fa-mobile-alt" />
                </div>
              )}
            </div>
            <div className="PwaInstallPrompt-info">
              <div className="PwaInstallPrompt-title">{appName}</div>
              <div className="PwaInstallPrompt-desc">{appDesc}</div>
            </div>
          </div>

          {this.showGuide && (
            <div className="PwaInstallPrompt-guide">
              {this.isIOS ? (
                <div>
                  <div className="PwaInstallPrompt-guide-step">
                    <span className="step-number">1</span>
                    <span>
                      اضغط على أيقونة المشاركة <i className="fas fa-share-square guide-icon" /> في شريط Safari
                    </span>
                  </div>
                  <div className="PwaInstallPrompt-guide-step">
                    <span className="step-number">2</span>
                    <span>
                      مرر للأسفل واختر <strong>«إضافة إلى الشاشة الرئيسية»</strong> <i className="fas fa-plus-square guide-icon" />
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="PwaInstallPrompt-guide-step">
                    <span className="step-number">1</span>
                    <span>
                      اضغط على زر خيارات المتصفح <i className="fas fa-ellipsis-v guide-icon" /> أعلى الشاشة
                    </span>
                  </div>
                  <div className="PwaInstallPrompt-guide-step">
                    <span className="step-number">2</span>
                    <span>
                      اختر <strong>«تثبيت التطبيق»</strong> أو <strong>«إضافة إلى الشاشة الرئيسية»</strong> <i className="fas fa-plus-square guide-icon" />
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="PwaInstallPrompt-actions">
            <button
              type="button"
              className="Button Button--link PwaInstallPrompt-btn-dismiss"
              onclick={() => this.dismiss()}
            >
              ليس الآن
            </button>
            <button
              type="button"
              className="Button Button--primary PwaInstallPrompt-btn-install"
              onclick={() => this.install()}
            >
              <i className={this.deferredPrompt ? 'fas fa-arrow-down Button-icon' : 'fas fa-plus-circle Button-icon'} />
              <span className="Button-label">
                {this.deferredPrompt
                  ? 'تثبيت التطبيق'
                  : this.showGuide
                  ? 'إخفاء التعليمات'
                  : 'طريقة التثبيت'}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }
}
